import React from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { Organizations } from '../types';
import { User } from '@app/shared/types';
import { Link } from 'react-router-dom';
import { useUserManagementContext } from '../context/UserManagementContext';
import {
  USER_MANAGEMENT_CLIENT_CREDENTIALS_READ,
  USER_MANAGEMENT_CLIENT_CREDENTIALS_WRITE,
} from '../constants';
import { enqueueSnackbar } from 'notistack';
import { useSendCredentials } from '../../CredentialsCard/sendCredentials';
import { FlexBox } from '../../global/FlexBox';
import { InfoItem, InfoItemTitle } from '../../InfoItem/UserInfoField';
import { ConfigApi, useApi } from '@backstage/core-plugin-api';
import { configApiRef } from '@backstage/core-plugin-api';
import { getApiErrorMessage } from '../../../shared/api/getApiErrorMessage';

interface UserDetailsInfoProps {
  userData?: User;
  isLoading: boolean;
  organizations?: Organizations;
}

const UserDetailsInfo = ({ userData, organizations }: UserDetailsInfoProps) => {
  const { loggedInUserData } = useUserManagementContext();
  const configApi: ConfigApi = useApi(configApiRef);
  const isSendClientCredentialsEnabled = configApi.getOptionalBoolean(
    'featureFlags.sendCredentials',
  );

  const { mutate: sendCredentials, isPending } = useSendCredentials(
    userData?.id,
    userData?.email,
  );

  const { setIsUserCredentialsDrawerOpen } = useUserManagementContext();

  const userHasReadPermission = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_CLIENT_CREDENTIALS_READ,
  );
  const userHasWritePermission = loggedInUserData?.attributes.includes(
    USER_MANAGEMENT_CLIENT_CREDENTIALS_WRITE,
  );

  const handleUpdateUser = () => {
    sendCredentials(undefined, {
      onSuccess: () => {
        enqueueSnackbar('Client credentials sent successfully', {
          variant: 'success',
        });
      },
      onError: (err: unknown) => {
        const msg = getApiErrorMessage(
          err,
          'Failed to send client credentials.',
        );
        enqueueSnackbar(msg, {
          variant: 'error',
        });
      },
    });
  };

  return (
    <>
      <Box style={{ marginBottom: 40 }}>
        <Breadcrumbs
          separator={
            <NavigateNextIcon
              fontSize="small"
              htmlColor="rgba(9, 34, 65, 0.7)"
            />
          }
        >
          <Link
            to="/user-management"
            style={{ textDecoration: 'none', color: 'inherit' }}
          >
            User Management
          </Link>
          <Typography variant="body1" color="textPrimary">
            {userData?.username}
          </Typography>
        </Breadcrumbs>
      </Box>
      <Typography variant="h6" color="textPrimary" style={{ marginBottom: 24 }}>
        User Details
      </Typography>
      <Box display="flex" flexDirection="column" style={{ gap: 24 }}>
        <InfoItem title="User Name" value={userData?.username} />
        <InfoItem title="Email Address" value={userData?.email} />
        <InfoItem title="Phone Number" value={userData?.phoneNumber} />
        <InfoItem title="Company" value={userData?.companyName} />
        <Box>
          <InfoItemTitle>Teams</InfoItemTitle>
          <Box
            display="flex"
            flexDirection="row"
            flexWrap="wrap"
            style={{ gap: 8 }}
          >
            {userData?.organizationIds?.length === 0 && <InfoItem />}
            {/* @ts-ignore */}
            {userData?.organizationIds?.map((orgId: string) => {
              const org = organizations?.data?.find(o => o.id === orgId);
              return (
                <Chip
                  color="primary"
                  label={org?.name || 'Unknown Team'}
                  key={orgId}
                />
              );
            })}
          </Box>
        </Box>
        {(userHasReadPermission || userHasWritePermission) && (
          <FlexBox
            direction="column"
            sx={{
              alignItems: 'flex-start',
            }}
          >
            <Typography
              variant="h6"
              color="textPrimary"
              style={{ marginBottom: 24 }}
            >
              Client Credentials
            </Typography>
            <FlexBox
              direction="column"
              sx={{
                alignItems: 'flex-start',
                gridRowGap: 16,
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                onClick={() => {
                  setIsUserCredentialsDrawerOpen(true);
                }}
              >
                {userHasWritePermission
                  ? 'Set/View Client Credentials'
                  : 'View Client Credentials'}
              </Button>
              {isSendClientCredentialsEnabled && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={handleUpdateUser}
                  disabled={isPending}
                >
                  {isPending ? (
                    <FlexBox>
                      <CircularProgress
                        size={20}
                        color="inherit"
                        style={{ marginRight: 8 }}
                      />
                      Sending...
                    </FlexBox>
                  ) : (
                    'Send Client Credentials'
                  )}
                </Button>
              )}
            </FlexBox>
          </FlexBox>
        )}
      </Box>
    </>
  );
};

export default UserDetailsInfo;
